import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COMPANY_DOMAINS } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import { useHttpClient } from "shared/hooks/http-hook";
import { DemoLeadCaptureContext } from "components/demo/DemoLeadCaptureContext";
import { trackSuiteEvent } from "shared/util/track";
import { clearStartStorage, markStartSeen, markEnterStartExperience } from "../start-flags";
import { INTENTS, SERVICE_DIRECTIONS, intentCreatesPassengers, intentCreatesRoutes, planSummary } from "../import/intent";
import ImportLive, { SampleOpening } from "./ImportLive";

const ROUTES_PATH = "/services?tab=vehicleRoutes";
const FIRST_ROUTE_PATH = `${ROUTES_PATH}&open=first`;
const STUDENTS_PATH = "/users?tab=students";
const DASHBOARD_PATH = "/dashboard";
// A store refresh that hangs (network down) must not leave a dead button; fall back to a full load.
const STORE_REFRESH_TIMEOUT_MS = 15000;
const SETUP_PATCH_TIMEOUT_MS = 8000;

const withDeadline = (promise, ms) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(false);
      },
    );
  });

const OPENING_COPY = {
  [FIRST_ROUTE_PATH]: "Opening your services…",
  [STUDENTS_PATH]: "Opening your students…",
  [DASHBOARD_PATH]: "Opening your dashboard…",
};

// Where a finished import lands: the first new service open, or the student list, or the board.
export const finishTargetFor = (result, intent) => {
  const routes = (result?.routes?.count || 0) + (result?.routes?.reusedCount || 0);
  if (routes > 0) return FIRST_ROUTE_PATH;
  if (intentCreatesPassengers(result?.intent || intent)) return STUDENTS_PATH;
  return DASHBOARD_PATH;
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many || `${one}s`}`;

const directionPhrase = (direction) => {
  if (direction === SERVICE_DIRECTIONS.PICKUP) return "morning pick-up";
  if (direction === SERVICE_DIRECTIONS.DROPOFF) return "afternoon drop-off";
  return "morning pick-up and afternoon drop-off";
};

export const describePlan = ({ intent, plan, extraction, includeRouteList, schoolName, schoolDecision, serviceDirection, servicePlan }) => {
  const lines = [];
  const stats = extraction?.stats || {};
  if (intentCreatesPassengers(intent)) {
    if (plan.add) lines.push(plan.add === 1 ? "1 new student and their parents are added" : `${plan.add} new students and their parents are added`);
    if (plan.update) lines.push(`${plural(plan.update, "registered student")} ${plan.update === 1 ? "gets" : "get"} refreshed details`);
    if (plan.skip) lines.push(`${plural(plan.skip, "registered student")} ${plan.skip === 1 ? "stays" : "stay"} untouched`);
    if (schoolDecision === "later") lines.push("Students go under “Default School” until you set one");
    else if (schoolName) lines.push(`School: ${schoolName}`);
  } else {
    if (plan.link) lines.push(`${plural(plan.link, "registered student")} ${plan.link === 1 ? "is" : "are"} placed on services`);
    if (plan.unmatched)
      lines.push(
        `${plural(plan.unmatched, "row")} ${plan.unmatched === 1 ? "matches nobody yet and is" : "match nobody yet and are"} left out`,
      );
  }
  if (intentCreatesRoutes(intent)) {
    const named = stats.distinctRouteNameCount || 0;
    const morning = servicePlan?.pickup?.length || 0;
    const afternoon = servicePlan?.dropoff?.length || 0;
    const counts = [morning ? plural(morning, "morning service") : "", afternoon ? plural(afternoon, "afternoon service") : ""]
      .filter(Boolean)
      .join(" and ");
    if (named) lines.push(`${counts || `${directionPhrase(serviceDirection)} services`} from the file — existing ones with the same name are reused`);
    else if (plan.add + plan.update + plan.skip + plan.link > 0)
      lines.push(`${counts || `${directionPhrase(serviceDirection)} services`}, grouped by school and vehicle capacity`);
    if (includeRouteList && stats.routeListWithoutPassengers) lines.push(`${plural(stats.routeListWithoutPassengers, "empty service")} from the route list`);
  } else {
    lines.push("No services are created");
  }
  return lines;
};

export const commitLabel = (intent, plan) => {
  if (intent === INTENTS.ROUTES_ONLY) return "Build the services";
  if (intent === INTENTS.PASSENGERS_ROUTES) return "Save passengers and create services";
  const count = plan.add + plan.update;
  return count ? `Save ${plural(count, "passenger")}` : "Save passengers";
};

const StageContinue = ({
  onBack,
  isDemo,
  source,
  intent,
  extraction,
  match,
  existingMode,
  existingDecisions,
  includeRouteList,
  serviceDirection,
  servicePlan,
  schoolName,
  schoolDecision,
  commitResult,
  commitError,
  existingStudentCount,
  busy,
  onCommit,
  onCreateWorkspace,
  onResumeCommit,
  pendingJob,
  handoffSession,
  jobProgress,
  workspaceStep,
  demoKind,
  fileName,
}) => {
  // An uploaded file and the sample roster are both written the same way.
  const isRoster = source === "upload" || source === "sample";
  const isSample = source === "sample";
  const plan = planSummary({ extraction, match, intent, existingMode, existingDecisions });
  const planLines = isRoster
    ? describePlan({ intent, plan, extraction, includeRouteList, schoolName, schoolDecision, serviceDirection, servicePlan })
    : [];
  const savedPassengers = Boolean(commitResult && intentCreatesPassengers(commitResult.intent || intent));
  const routeCount = (commitResult?.routes?.count || 0) + (commitResult?.routes?.reusedCount || 0);
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);
  const { openKeepAccount } = useContext(DemoLeadCaptureContext);
  const { sendRequest } = useHttpClient();
  const navigate = useNavigate();
  const [localBusy, setLocalBusy] = useState(false);
  const [error, setError] = useState(null);
  const storeFreshRef = useRef(false);
  const storeRefreshRef = useRef(null);

  const ensureStore = async () => {
    if (storeFreshRef.current) return true;
    // Not loaded yet (fresh session): App is fetching it and CheckAccess waits for it — nothing to redo.
    if (store && store.storeReady === false) return true;
    if (!storeRefreshRef.current) {
      storeRefreshRef.current = withDeadline(
        Promise.resolve()
          .then(() => (typeof store?.UpdateEverything === "function" ? store.UpdateEverything() : null))
          .then(() => true),
        STORE_REFRESH_TIMEOUT_MS,
      ).then((ok) => {
        if (ok) storeFreshRef.current = true;
        else storeRefreshRef.current = null;
        return ok;
      });
    }
    return storeRefreshRef.current;
  };

  const openWorkspace = async (path) => {
    markStartSeen(auth.id);
    const ok = await ensureStore();
    if (ok) {
      navigate(path);
      return;
    }
    window.location.assign(path);
  };

  // Real sign-ups still owe a transport type; set it to students so the dashboard's setup modal
  // does not greet them right after the import. Demo workspaces are created already set up.
  const settleTransportType = async () => {
    if (!(auth.isLoggedIn && auth.client?.onboardingStatus && !auth.client.onboardingStatus.finishedMetaSetup)) return;
    await withDeadline(
      sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/${auth.id}/transport-type`,
        "PATCH",
        JSON.stringify({ transportType: COMPANY_DOMAINS.STUDENT }),
        { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      ).then(() => (auth.login ? auth.login(auth.id, auth.token) : null)),
      SETUP_PATCH_TIMEOUT_MS,
    );
  };

  const doneHeadline = () => {
    if (!commitResult) return "";
    const persist = commitResult.persist || {};
    const parts = [];
    if (persist.studentCount) parts.push(plural(persist.studentCount, "student") + " added");
    if (persist.updatedCount) parts.push(plural(persist.updatedCount, "student") + " refreshed");
    if (persist.parentCount) parts.push(plural(persist.parentCount, "parent") + " added");
    if (commitResult.routes?.count) parts.push(plural(commitResult.routes.count, "service") + " created");
    if (commitResult.routes?.reusedCount) parts.push(plural(commitResult.routes.reusedCount, "existing service") + " updated");
    if (!parts.length) return "Nothing needed to change.";
    return `${parts.join(", ")}.`;
  };

  // An upload that has been written is finished: hand the user the result itself — the first
  // new service open, or the student list — instead of a summary screen with more buttons.
  // The journey is cleared first so coming back to /start starts clean instead of re-firing this.
  const finishedRef = useRef(false);
  const [finishing, setFinishing] = useState(() => Boolean(commitResult && isRoster && auth.isLoggedIn));
  useEffect(() => {
    if (finishedRef.current || !commitResult || !isRoster || !auth.isLoggedIn) return;
    finishedRef.current = true;
    setFinishing(true);
    const target = finishTargetFor(commitResult, intent);
    trackSuiteEvent("Start — Import finished", "navigate", { key: "start_import_finished", target });
    const summary = doneHeadline();
    const geocodeFailures = commitResult.persist?.geocodeFailureCount || 0;
    (async () => {
      await settleTransportType();
      markStartSeen(auth.id);
      const ok = await ensureStore();
      clearStartStorage();
      if (summary && summary !== "Nothing needed to change.") window.toast?.success?.(summary, { autoClose: 6000 });
      // Passengers were written but no services could be built (e.g. nobody registered yet).
      if (commitResult.warning?.message) window.toast?.warn?.(commitResult.warning.message, { autoClose: 10000 });
      if (geocodeFailures) {
        window.toast?.warn?.(
          `${geocodeFailures} ${geocodeFailures === 1 ? "address" : "addresses"} could not be placed on the map — fix them on the student cards.`,
          { autoClose: 10000 },
        );
      }
      if (ok) navigate(target, { replace: true });
      else window.location.assign(target);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitResult, source, auth.isLoggedIn]);

  // A visitor whose roster is already written but whose login did not land (network blip): retry that
  // same workspace instead of opening another one.
  const handoffTriedRef = useRef(false);
  useEffect(() => {
    if (handoffTriedRef.current || auth.isLoggedIn || !handoffSession?.token || !commitResult) return;
    handoffTriedRef.current = true;
    Promise.resolve(auth.login?.(handoffSession.id, handoffSession.token)).catch(() => {
      /* the marker stays, so a later visit can try again */
    });
  }, [auth, handoffSession, commitResult]);

  // A save that was still running when this page went away (refresh, closed tab, or the login
  // remount of the visitor path) is picked up here — once — instead of being started again.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !pendingJob || busy || !isRoster || typeof onResumeCommit !== "function") return;
    resumedRef.current = true;
    trackSuiteEvent("Start — Resume import", "auto", { key: "start_import_resumed" });
    onResumeCommit({ login: auth.login }).catch(() => {
      /* the journey records the error; nothing else to do here */
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingJob, busy, source]);

  const createLive = async (confirmExistingOperation = true) => {
    setError(null);
    trackSuiteEvent("Start — Commit import", "button_click");
    try {
      await onCommit({ confirmExistingOperation });
      await ensureStore();
    } catch (err) {
      if (err?.code !== "CONFIRM_EXISTING") {
        setError(err?.payload?.message || err?.message || "The operation could not be created.");
      }
    }
  };

  // Visitor (or seeded-demo user): open an empty workspace and save straight into it.
  const createWorkspace = async () => {
    setError(null);
    trackSuiteEvent("Start — Create workspace from upload", "button_click", { key: "start_workspace_created" });
    try {
      await onCreateWorkspace({ sendRequest, login: auth.login, search: window.location.search });
    } catch (err) {
      if (err?.code === 429) {
        setError("Too many workspaces were opened from this network just now. Log in instead, or try again in a few minutes.");
      } else {
        setError(err?.payload?.message || err?.message || "The workspace could not be created.");
      }
    }
  };

  const goToLogin = () => {
    markEnterStartExperience();
    navigate("/login");
  };

  const isWorkspaceDemo = Boolean(isDemo && demoKind === "workspace");
  const isSeededDemo = Boolean(isDemo && demoKind !== "workspace");

  // The sample needs no confirmation: it is written the moment it is ready. Visitors (and the
  // seeded live demo, which is full of other data) get a private student-only workspace; a
  // workspace demo already is one, so the sample goes straight into it.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current || !isSample || commitResult || commitError || busy || pendingJob || !extraction) return;
    // A signed-in operator's own company is not a place to silently drop the sample roster.
    // They add a file from the student list; the sample is for visitors and demo accounts.
    if (auth.isLoggedIn && !isDemo) return;
    autoStartedRef.current = true;
    trackSuiteEvent("Start — Sample saved", "auto", { key: "start_sample_saved" });
    if (auth.isLoggedIn && isWorkspaceDemo) createLive(true);
    else if (onCreateWorkspace) createWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSample, commitResult, commitError, busy, pendingJob, extraction]);

  const goToRoutes = async () => {
    trackSuiteEvent("Start — Open new routes", "button_click");
    setLocalBusy(true);
    try {
      await openWorkspace(ROUTES_PATH);
    } finally {
      setLocalBusy(false);
    }
  };

  const goToStudents = async () => {
    trackSuiteEvent("Start — Open students", "button_click");
    setLocalBusy(true);
    try {
      await openWorkspace(STUDENTS_PATH);
    } finally {
      setLocalBusy(false);
    }
  };

  // While the save job runs the whole stage is the live view; buttons and plan copy step aside.
  const saving = isRoster && !commitResult && (Boolean(jobProgress) || Boolean(workspaceStep) || (Boolean(pendingJob) && busy));

  return (
    <section className="start-stage">
      {saving && isSample && <SampleOpening fileName={fileName} />}
      {saving && !isSample && <ImportLive progress={jobProgress} workspaceStep={workspaceStep} intent={intent} fileName={fileName} />}
      {!saving && (
      <header className="start-stage__header">
        <p className="start-kicker">{commitResult ? "Done" : "Last step"}</p>
        <h2>
          {commitResult
            ? savedPassengers
              ? routeCount
                ? "Passengers and services are live"
                : "Passengers are saved"
              : "Services are ready"
            : isSample
              ? "Saving the sample roster"
              : "Ready when you are"}
        </h2>
        <p>
          {commitResult
            ? finishing
              ? `${doneHeadline()} ${OPENING_COPY[finishTargetFor(commitResult, intent)] || OPENING_COPY[DASHBOARD_PATH]}`
              : doneHeadline()
            : isSample
              ? "85 students across three schools go into a private workspace of your own, with their morning and afternoon services."
              : "Here is exactly what will happen. Nothing is written until you press the button."}
        </p>
      </header>
      )}

      {isRoster && auth.isLoggedIn && !commitResult && !saving && (
        <div className="start-commit">
          <ul className="start-plan">
            {planLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {isSeededDemo && (
            <p className="start-note start-note--warn">
              You are in the sample workspace, which is full of demo data and is cleared when you keep the account. To keep this
              file, save it into a clean workspace of its own.
            </p>
          )}
          {existingStudentCount > 0 && !isSeededDemo && (
            <p className="start-note start-note--warn">
              This company already has {existingStudentCount} students. Confirm to add this roster beside them.
            </p>
          )}
          <div className="start-stage__nav-end start-commit__actions">
            {isSeededDemo && onCreateWorkspace && (
              <button type="button" className="start-btn" onClick={createWorkspace} disabled={busy}>
                {workspaceStep === "creating" ? "Opening a clean workspace…" : workspaceStep === "saving" ? "Saving…" : "Save into a clean workspace"}
              </button>
            )}
            <button
              type="button"
              className={`start-btn${isSeededDemo ? " start-btn--ghost" : ""}`}
              onClick={() => createLive(true)}
              disabled={busy}
            >
              {busy && !workspaceStep
                ? "Working…"
                : isSeededDemo
                  ? "Save here anyway (sample workspace)"
                  : existingStudentCount > 0
                    ? "Yes, add this roster beside existing data"
                    : commitLabel(intent, plan)}
            </button>
          </div>
          {busy && (
            <p className="start-muted start-commit__busy" role="status">
              {workspaceStep === "creating"
                ? "Opening your workspace."
                : intentCreatesPassengers(intent)
                  ? "Saving passengers and placing their addresses on the map. Larger lists take up to a minute."
                  : "Building the services."}
            </p>
          )}
        </div>
      )}

      {isRoster && !auth.isLoggedIn && !commitResult && !saving && (
        <div className="start-commit">
          <ul className="start-plan">
            {planLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <button type="button" className="start-btn" onClick={createWorkspace} disabled={busy || !onCreateWorkspace}>
            {workspaceStep === "creating"
              ? "Opening your workspace…"
              : workspaceStep === "saving"
                ? "Saving…"
                : isSample
                  ? "Save the sample roster"
                  : commitLabel(intent, plan)}
          </button>
          <p className="start-muted start-commit__busy" role={busy ? "status" : undefined}>
            {busy
              ? workspaceStep === "creating"
                ? "Opening a private workspace for this file."
                : "Saving passengers and placing their addresses on the map. Larger lists take up to a minute."
              : isSample
                ? "No sign-up needed. A private workspace is opened for the sample; keep it later with your e-mail."
                : "No sign-up needed. A private workspace is opened for this file; keep it later with your e-mail. Your list also stays in this browser for two days."}
          </p>
          <p className="start-muted">
            Already have an account?{" "}
            <button type="button" className="start-text-link" onClick={goToLogin}>
              Log in
            </button>{" "}
            and this file follows you.
          </p>
        </div>
      )}

      {commitResult && !finishing && (
        <div className="start-commit start-commit--done">
          {(commitResult.persist?.skippedCount > 0 ||
            commitResult.persist?.linkedCount > 0 ||
            commitResult.persist?.unmatchedCount > 0 ||
            commitResult.routes?.alreadyRoutedCount > 0) && (
            <p className="start-muted">
              {commitResult.persist.linkedCount > 0 &&
                `${plural(commitResult.persist.linkedCount, "registered student")} placed on the services named in the file. `}
              {commitResult.persist.skippedCount > 0 && `${plural(commitResult.persist.skippedCount, "registered student")} left untouched. `}
              {commitResult.persist.unmatchedCount > 0 &&
                `${plural(commitResult.persist.unmatchedCount, "row")} matched nobody and ${commitResult.persist.unmatchedCount === 1 ? "was" : "were"} left out. `}
              {commitResult.routes?.alreadyRoutedCount > 0 &&
                `${plural(commitResult.routes.alreadyRoutedCount, "passenger")} without a service name already ride one, so nothing new was made for them.`}
            </p>
          )}
          {(commitResult.routes?.names || []).length > 0 && (
            <ul>
              {commitResult.routes.names.slice(0, 8).map((name) => (
                <li key={name}>{name}</li>
              ))}
              {commitResult.routes.names.length > 8 && <li className="start-muted">+{commitResult.routes.names.length - 8} more</li>}
            </ul>
          )}
          {commitResult.persist?.geocodeFailureCount > 0 && (
            <p className="start-note start-note--warn">
              {commitResult.persist.geocodeFailureCount} addresses could not be located on the map. You can fix them on the student cards.
            </p>
          )}
          {isWorkspaceDemo && (
            <p className="start-note">
              This workspace is yours for 7 days. Keep it for good with your e-mail — nothing here is removed when you do.
            </p>
          )}
          <div className="start-stage__nav-end">
            {savedPassengers && (
              <button type="button" className="start-btn start-btn--ghost" onClick={goToStudents} disabled={localBusy || busy}>
                {localBusy ? "Opening…" : "Open the students"}
              </button>
            )}
            {routeCount > 0 && (
              <button
                type="button"
                className={`start-btn${isWorkspaceDemo ? " start-btn--ghost" : ""}`}
                onClick={goToRoutes}
                disabled={localBusy || busy}
              >
                {localBusy ? "Opening…" : "Open the services"}
              </button>
            )}
            {isWorkspaceDemo && (
              <button
                type="button"
                className="start-btn"
                onClick={() => {
                  trackSuiteEvent("Start — Keep workspace", "button_click", { key: "start_keep_workspace" });
                  if (openKeepAccount) openKeepAccount();
                }}
              >
                Keep this workspace
              </button>
            )}
          </div>
        </div>
      )}

      {(error || commitError) && (
        <p className="start-error" role="alert">
          {error || commitError}
        </p>
      )}
      {!finishing && !saving && (
        <div className="start-stage__nav">
          <button type="button" className="start-btn start-btn--ghost" onClick={onBack}>
            Back
          </button>
        </div>
      )}
    </section>
  );
};

export default StageContinue;
